<script setup>
import { useTrackPageView } from '~/composables/gtm/useTrackPageView'
import { useRobotsMeta } from '~/composables/seo/robotsMeta'
import { useOscarClient } from '~/composables/useOscarClient'
import { useSearchStore } from '~/stores/search'
import { useSeoStore } from '~/stores/seo'

const { t, locale } = useI18n()
const oscar = useOscarClient(locale.value)
const authStore = useAuthStore()
const { trackPageView } = useTrackPageView()

trackPageView('app.Search', { title: 'Search' }, authStore?.user?.id)

const productsLoading = ref(true)
const otherLoading = ref(true)

const searchStore = useSearchStore()
const { filter } = storeToRefs(searchStore)

const route = useRoute()
const seoStore = useSeoStore()

const productPage = ref(1)

const results = ref(null)
const productResults = ref([])

const PRODUCT_SEARCH_RE = /^(?!.*!products).*products.*$/
const LEADING_COMMA_RE = /^,/
const TRAILING_COMMA_RE = /,$/

const loading = computed(() => {
  return productsLoading.value || otherLoading.value
})

const total = computed(() => {
  const totalOthers = results.value?._meta?.total ?? 0
  const totalProducts = productResults.value?._meta?.total ?? 0

  return totalOthers + totalProducts
})

function displaySectionName(data, isSearchResults = false) {
  if (data?._meta?.total === 1) {
    return data?._meta?.verbose_name || ''
  }
  else {
    if (
      data?._meta?.verbose_name_plural === 'Produkte'
      && isSearchResults
    ) {
      return 'Produkten'
    }
    return data?._meta?.verbose_name_plural || ''
  }
}

const sections = computed(() => {
  let res = []

  if (productResults.value?.data) {
    res = productResults.value?.data
  }

  if (results.value?.data) {
    res = res.concat(results.value?.data)
  }

  return res
})

const queryString = computed(() => {
  return route.query.q
})

const filterQueryString = computed(() => {
  return route.query.filter || ''
})

watch([queryString, filterQueryString], ([newVal]) => {
  search(newVal)
})

function loadNext(page) {
  productPage.value = page
  searchProducts(queryString.value)
}

function mergeProductResults(data) {
  if (productPage.value === 1) {
    productResults.value = data
  }
  else {
    const currentData = productResults.value?.data || []
    const nextData = data?.data || []

    const currentProductSection = currentData.find(
      section => section?._meta?.type === 'product',
    )
    const nextProductSection = nextData.find(
      section => section?._meta?.type === 'product',
    )

    const mergedProductSection = currentProductSection && nextProductSection
      ? {
          ...currentProductSection,
          _meta: {
            ...currentProductSection._meta,
            ...nextProductSection._meta,
          },
          results: [
            ...(currentProductSection.results || []),
            ...(nextProductSection.results || []),
          ],
        }
      : (nextProductSection || currentProductSection)

    const preservedNonProductSections = currentData.filter(
      section => section?._meta?.type !== 'product',
    )
    const fallbackNonProductSections = nextData.filter(
      section => section?._meta?.type !== 'product',
    )
    const nonProductSections = preservedNonProductSections.length > 0
      ? preservedNonProductSections
      : fallbackNonProductSections

    productResults.value = {
      ...productResults.value,
      _meta: {
        ...productResults.value?._meta,
        ...data?._meta,
      },
      data: [
        ...(mergedProductSection ? [mergedProductSection] : []),
        ...nonProductSections,
      ],
    }
  }
}

async function searchProducts(q) {
  productsLoading.value = true

  try {
    const res = await oscar.search(q, 'products', productPage.value)
    mergeProductResults(res.data)
  }
  catch (e) {
    console.error(e)
  }
  finally {
    productsLoading.value = false
  }
}

async function search(q) {
  otherLoading.value = true
  productsLoading.value = true

  results.value = null
  productPage.value = 1
  productResults.value = []

  if (q) {
    const productSearch = PRODUCT_SEARCH_RE.test(filterQueryString.value) || !filterQueryString.value

    if (productSearch) {
      await searchProducts(q)
    }
    else {
      productsLoading.value = false
    }

    if (filterQueryString.value !== 'products') {
      const validQueryString = filterQueryString.value
        .replace('!products', '')
        .replace('products', '')
        .replace(LEADING_COMMA_RE, match => (match === ',' ? '' : match))
        .replace(TRAILING_COMMA_RE, '')

      try {
        const res = await oscar.search(
          q,
          `${validQueryString}${!validQueryString ? '!products' : ',!products'}`,
        )
        results.value = res.data
      }
      catch (e) {
        console.error(e)
      }
      finally {
        otherLoading.value = false
      }
    }
    else {
      otherLoading.value = false
    }
  }
}

onMounted(() => {
  seoStore.pageType = ''
  search(queryString.value)
})

useHead({
  link: {
    hid: 'canonical',
    rel: 'canonical',
    href: route.path,
  },
})
useRobotsMeta('noindex', false)
useHead({
  title: route.query.q
    ? t('magazine.overview.searchTitleQ', { search: route.query.q })
    : t('magazine.overview.searchTitle'),
})
</script>

<template>
  <div class="row" data-cy="search">
    <div class="col-12 spacer-navigation spacer-footer">
      <h1 class="scale-6 spacer-40">
        {{ t('search.title') }}
      </h1>
      <SearchFakeInput
        :q="queryString"
        class="spacer-80-60 search-input"
      />
      <div v-if="total > 0">
        <div
          v-for="section in sections"
          :key="section._meta.verbose_name"
          class="spacer-search"
        >
          <div
            v-if="
              (section?._meta?.total > 0
                && filter
                  .map((f) => f.verbose_name)
                  .includes(
                    section?._meta?.verbose_name_plural,
                  ))
                || section?._meta?.type === 'category'
                || (section?._meta?.total > 0 && filter?.length === 0)
            "
          >
            <h2
              class="scale-3 spacer-40"
              data-cy="search-results-found"
            >
              {{ section?._meta?.total }}
              {{ displaySectionName(section) }}
              {{ t('search.found') }}
            </h2>
            <div
              v-if="section?._meta?.type === 'product'"
              class="safari-grid-fix"
            >
              <SearchResultsProduct
                :name="displaySectionName(section, true)"
                :page="productPage"
                :size="6"
                :pages="Math.ceil(section?._meta?.total / 6)"
                :total="section?._meta?.total"
                :items="section?.results"
                :loading="productsLoading"
                @more="loadNext"
              />
              <div
                v-if="productsLoading"
                class="mt-4"
              >
                <LoadingIndicator />
              </div>
            </div>
            <div
              v-if="otherLoading"
              class="mt-4"
            >
              <LoadingIndicator />
            </div>
            <div
              v-else-if="
                section?._meta?.type === 'magazine'
                  || section?._meta?.type === 'category'
              "
            >
              <SearchResultsGallery
                :name="displaySectionName(section)"
                :page="1"
                :size="6"
                :pages="Math.ceil(section?._meta?.total / 6)"
                :total="section?._meta?.total"
                :items="section?.results"
              />
            </div>
          </div>
          <div
            v-else-if="
              filter?.length === 0 && section?._meta?.type === 'product'
            "
            class="scale-3 scale-3--bold no-results"
          >
            {{ t('search.noResults.no') }}
            {{ section?._meta?.verbose_name_plural }}
            {{ t('search.noResults.withSearchTerms') }}
          </div>
        </div>
      </div>
      <div v-else-if="loading">
        <LoadingIndicator />
      </div>
      <div
        v-else
        class="scale-3 scale-3--bold no-results"
        data-cy="search-no-results"
      >
        {{ t('search.noResults.overall') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '~/assets/scss/variables.scss';
@import 'bootstrap/scss/mixins';

.search-input {
  width: 66%;

  @include media-breakpoint-down(560) {
    width: 100%;
  }
}

.safari-grid-fix {
  display: grid;
}

.results-category {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 40px;
  row-gap: clamp(20px, 1.39vw + 15.56px, 40px);
  margin: 0;

  @include media-breakpoint-down(640) {
    grid-template-columns: 1f;
  }

  &__item {
    height: clamp(425px, w + 372.78px, 660px);
    width: 100%;
  }
}

.no-results {
  border: 1px solid $color-disabled;
  border-radius: 12px;
  padding: 35px 20px;
  text-align: center;

  @include media-breakpoint-down(560) {
    padding: 40px;
  }
}

.spacer-search {
  margin-bottom: clamp(120px, 4.17vw + 106.67px, 180px);
}
</style>
